import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdlRecord } from "../../../store/slices/adlRecordsSlice";
import logo from "../../../assets/images/logo-samplee.png";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${d.getFullYear()}`;
};

export default function ADLRecordPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { currentAdlRecord, isLoading, error } = useSelector((state) => state.adlRecords);

  useEffect(() => {
    if (id) dispatch(fetchAdlRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentAdlRecord?.record) {
      const timer = setTimeout(() => window.print(), 400);
      window.onafterprint = () => window.close();
      return () => clearTimeout(timer);
    }
  }, [currentAdlRecord]);

  if (isLoading) return <div className="p-4 text-center">Loading…</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentAdlRecord?.record) return <div className="p-4 text-center">No record found.</div>;

  const rec = currentAdlRecord.record;
  const activityRecords = rec.activity_records || [];

  // Step 1: Get unique dates (sorted)
  const uniqueDates = [...new Set(activityRecords.map((r) => formatDate(r.date)))].sort(
    (a, b) => new Date(a.split("/").reverse().join("-")) - new Date(b.split("/").reverse().join("-"))
  );

  // Step 2: Max 14 days
  const dateColumns = uniqueDates.slice(0, 14);
  const totalDateCols = dateColumns.length;

  // Dynamic column width
  const colWidth = totalDateCols > 0 ? `${82 / totalDateCols}%` : "5%";

  // Step 3: Group by activity_id
  const activityMap = {};
  activityRecords.forEach((r) => {
    const actId = r.activity_id;
    if (!activityMap[actId]) {
      activityMap[actId] = {
        name: r.activity.name,
        sort_order: r.activity.sort_order || 0,
        scores: {},
      };
    }
    const dateKey = formatDate(r.date);
    activityMap[actId].scores[dateKey] = {
      score: r.score,
      totalScore: r.total_score,
      staffName: r.staff_name,
    };
  });

  // Step 4: Sort activities
  const activities = Object.values(activityMap).sort((a, b) => a.sort_order - b.sort_order);

  // Pre-calculate total score and staff per activity
  const activityStats = activities.map((act) => {
    let totalScore = 0;
    const staffSet = new Set();

    dateColumns.forEach((date) => {
      const scoreData = act.scores[date] || {};
      if (scoreData.score) totalScore += scoreData.score;
      if (scoreData.staffName) staffSet.add(scoreData.staffName.trim());
    });

    return {
      ...act,
      totalScore,
      staffNames: Array.from(staffSet).join(", ") || "",
    };
  });

  return (
    <>
      {/* Unified Print Styles */}
      <style jsx global>{`
        * {
          text-align: left !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }

        body {
          font-family: Arial, sans-serif !important;
          line-height: 1.6 !important;
          color: #000000 !important;
          background-color: #ffffffff !important;
          font-size: 10pt !important;
        }

        .print-container {
          display: block !important;
          width: 100% !important;
          padding: 20px !important;
          position: relative !important;
        }

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }

        .logo img {
          height: 60px !important;
          width: auto !important;
        }

        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: 600 !important;
          color: #000000 !important;
        }

        .info-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 14px !important;
          font-size: 1rem !important;
        }

        .underline {
          display: inline-block !important;
          width: auto !important;
          border-bottom: 1px solid #000 !important;
          margin-left: 5px !important;
        }
        .underline-small { max-width: 200px !important; }
        .underline-date { max-width: 80px !important; }

        .instruction {
          margin: 15px 0 !important;
          font-size: 11pt !important;
        }

        .instruction p {
          font-weight: bold !important;
          margin-bottom: 5px !important;
        }

        .instruction ul {
          list-style: none !important;
          padding-left: 20px !important;
          margin: 5px 0 !important;
        }

        .instruction li {
          margin-bottom: 3px !important;
        }

        .main-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 15px !important;
          font-size: 9pt !important;
        }

        .main-table th,
        .main-table td {
          border: 0.75pt solid #000 !important;
          padding: 4px 2px !important;
          text-align: center !important;
          vertical-align: middle !important;
        }

        .main-table th {
          background-color: #d0cece !important;
          font-weight: bold !important;
        }

        .activity-name {
          text-align: left !important;
          font-weight: 600 !important;
          padding-left: 8px !important;
        }

        .checkbox {
          display: inline-block !important;
          width: 12px !important;
          height: 12px !important;
          border: 1px solid #000 !important;
          margin: 0 2px !important;
          line-height: 12px !important;
          font-size: 10px !important;
          font-weight: bold !important;
        }

        .checked::before {
          content: "✓" !important;
        }

        .footer {
          margin-top: 30px !important;
          border-top: 1.5px solid #000 !important;
          padding-top: 8px !important;
          font-size: 1em !important;
        }

        .footer p {
          text-align: center !important;
          margin-bottom: 6px !important;
        }

        .footer-meta,
        .footer-sign {
          display: flex !important;
          justify-content: space-between !important;
          margin-top: 12px !important;
          font-size: 0.9em !important;
        }

        @media print {
          .print-container {
            padding: 15px !important;
          }
        }
      `}</style>

      <div className="logo">
        <img src={logo} alt="The Recovery House" />
      </div>

      <div className="print-container">
        {/* Header */}
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>ADL RECORD SHEET</h6>
        </div>
        <br />

        {/* Client Info */}
        <div className="info-row">
          <div>
            <span>Client Name:</span>
            <span className="underline fw-bold">{rec.client_name || " "}</span>
          </div>
          <div>
            <span>Room No.:</span>
            <span className="underline fw-bold">{rec.room_number || " "}</span>
          </div>
          <div>
            <span>Date Range:</span>
            <span className="underline fw-bold">
              {rec.date_range?.split("-")[0]?.trim() || " "}
            </span>
          </div>
        </div>

        {/* Instruction */}
        <div className="instruction">
          <p><strong>Instruction: Scoring System</strong></p>
          <ul>
            <li><strong>1 = Independent</strong></li>
            <li><strong>2 = Supervision</strong></li>
            <li><strong>3 = Totally Dependent</strong></li><div/>
            <li>
              <input type="checkbox" style={{ marginRight: "8px" }} />
              <strong>Independent (I)</strong>: Client performs the activity independently. (Score 1)
            </li>
            <li>
              <input type="checkbox" style={{ marginRight: "8px" }} />
              <strong>Supervision (S)</strong>: Client performs the activity with supervision. (Score 2)
            </li>
            <li>
              <input type="checkbox" style={{ marginRight: "8px" }} />
              <strong>Dependent (D)</strong>: Client requires assistance to perform the activity. (Score 3)
            </li>
          </ul>
        </div>

        {/* DYNAMIC TABLE */}
        <table className="main-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: "15%" }}>Activity</th>
              {dateColumns.map((date, i) => (
                <th key={i} colSpan={3} style={{ width: colWidth }}>
                  {date}
                </th>
              ))}
              <th rowSpan={2}>Total Score (Out of 135)</th>
              <th rowSpan={2}>Staff Name</th>
            </tr>
          </thead>

          <tbody>
            {activityStats.map((act, index) => (
              <tr key={index}>
                <td className="activity-name" style={{ width: "15%" }}>
                  {act.name}
                </td>

                {dateColumns.map((date, colIdx) => {
                  const scoreData = act.scores[date] || {};
                  return (
                    <td key={`${index}-${colIdx}`} colSpan={3} style={{ width: colWidth }}>
                      {/* 1 */}
                      <span style={{ marginRight: "2px" }}>
                        <span>1</span>
                        <span className={`checkbox ${scoreData.score === 1 ? "checked" : ""}`} />
                      </span>
                      {/* 2 */}
                      <span style={{ marginRight: "2px" }}>
                        <span>2</span>
                        <span className={`checkbox ${scoreData.score === 2 ? "checked" : ""}`} />
                      </span>
                      {/* 3 */}
                      <span>
                        <span>3</span>
                        <span className={`checkbox ${scoreData.score === 3 ? "checked" : ""}`} />
                      </span>
                    </td>
                  );
                })}

                <td style={{ width: colWidth, textAlign: "center" }}>
                  {act.totalScore}
                </td>
                <td style={{ width: colWidth, textAlign: "center" }}>
                  {act.staffNames}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer */}
        <div className="footer">
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan Ph : 021-34546364-65-66</p>
          <p>A Project of <strong>“CARVAN OF LIFE PAKISTAN TRUST”</strong></p>

          <div className="footer-sign" style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
            <div>
              <strong>Checked by:</strong> <span className="underline">{rec.checked_by || ""}</span>
            </div>
            <div>
              <strong>Date:</strong> <span className="underline">{formatDate(rec.created_at) || " "}</span>
            </div>
          </div>

          <div className="footer-sign" style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
            <div>
              <strong>Nursing Manager / Head Nurse:</strong>{" "}
              <span className="underline" style={{ minWidth: "200px", display: "inline-block" }}>
                {rec.nursing_manager || " "}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}